/**
 * Where a session works (RFC 0277 S14), for any surface that has a session id.
 *
 * The data and the moves behind `WhereChip`, in one place, because three
 * surfaces draw it: the thread's header, the thread's composer once it is
 * compact over a file, and the shell's composer strip talking to a session it
 * is not showing. A second copy of "which worktree is this session in" is how
 * two of them come to disagree.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  addSessionProject,
  noteSessionBranch,
  projectSelectedDevice,
  projectSessionCode,
  projectSources,
  removeSessionProject,
  type SessionCheckout,
  selectSessionDevice,
  sessionAddedProjects,
  sessionCheckoutFor,
  setSessionCheckout,
  setSessionCode,
} from '@mod/core/model'
import { Box, GitBranch, Plus, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  addProjectWorktree,
  type CodeWorktree,
  useProjectWorktrees,
} from '../../services/code/worktrees'
import { useDocument } from '../../services/world/repo/useDocument'
import { useWorldDevices } from '../../services/world/useWorldDevices'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import { WhereChip, type WhereProject } from './WhereChip'

export function SessionWhere({
  sessionId,
  projectId,
  opens = 'down',
  projects = [],
  onSelectProject,
}: {
  sessionId: string
  projectId: string | undefined
  /** Which way the card opens: down from a header, up from a composer. */
  opens?: 'down' | 'up'
  /** Projects to move to, and the move; given only before the first message. */
  projects?: WhereProject[]
  onSelectProject?: (projectId: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const [sessionDoc] = useDocument(sessionId as AnyDocumentId)
  const [projectDoc] = useDocument(projectId as AnyDocumentId | undefined)
  const presentDevices = useWorldDevices()
  const worldProjects = useWorldProjects()
  const selectedDeviceId = projectSelectedDevice(sessionDoc)
  const added = sessionAddedProjects(sessionDoc)
  // RFC 0362 3b: the session's main source (its cwd), and the checkout it
  // has there. Read through the per-source checkouts, which also read an
  // older session's single `code`.
  const { defaultSourceId, sources } = projectSources(projectDoc)
  const legacy = projectSessionCode(sessionDoc)
  const codeSourceId = (sessionDoc as { codeSourceId?: unknown } | undefined)
    ?.codeSourceId
  const mainSourceId =
    codeSourceId != null
      ? String(codeSourceId)
      : (legacy?.sourceId ?? defaultSourceId)
  const home = { projectId: projectId ?? '', defaultSourceId }
  const code = projectId
    ? sessionCheckoutFor(sessionDoc, home, projectId, mainSourceId)
    : null
  const { trees, reload } = useProjectWorktrees(
    projectId,
    undefined,
    sources.length > 1 ? mainSourceId : undefined,
  )

  // Moving the session into a worktree records its branch and the head it has
  // out; a branch the session already had keeps the base it forked from. The
  // main source's checkout is written both ways, so readers of the older
  // single `code` (the review, the changes panel) still see it.
  const onSelectWorktree = useCallback(
    (tree: CodeWorktree) => {
      if (!tree.branch || !tree.head || !projectId) return
      const { branch, head } = tree
      const checkout: SessionCheckout = {
        vcs: 'git',
        branch,
        baseCommit: code?.branch === branch ? code.baseCommit : head,
        headCommit: head,
      }
      // The single `code` record, which readers of it (the review, the CLI)
      // still use; writing it clears any per-source entry for this source,
      // so the newer write is the one placement reads.
      void setSessionCode(repo, sessionId, {
        ...checkout,
        sourceId: mainSourceId,
      })
        .then(() => noteSessionBranch(repo, projectId, sessionId, branch))
        .catch(() => {
          // best-effort; the doc re-reads on change either way
        })
    },
    [repo, projectId, sessionId, code, mainSourceId],
  )
  // A new worktree on a new branch started from the current one, named for
  // the session so it is findable in git.
  const onNewWorktree = useCallback(
    async (from: string) => {
      if (!projectId) return
      const tree = await addProjectWorktree(
        projectId,
        `mod/session/${sessionId.slice(0, 8)}`,
        from,
        sources.length > 1 ? mainSourceId : undefined,
      )
      reload()
      onSelectWorktree(tree)
    },
    [projectId, sessionId, reload, onSelectWorktree, sources, mainSourceId],
  )
  // `check out here` (S4): the branch has no worktree on this device.
  const onCheckOutHere = useCallback(async () => {
    if (!code?.branch || !projectId) return
    await addProjectWorktree(
      projectId,
      code.branch,
      undefined,
      sources.length > 1 ? mainSourceId : undefined,
    )
    reload()
  }, [code, projectId, reload, sources, mainSourceId])
  const onSelectDevice = useCallback(
    (deviceId: string) => {
      void selectSessionDevice(repo, sessionId, deviceId).catch(() => {
        // best-effort; the doc re-reads on change either way
      })
    },
    [repo, sessionId],
  )

  // Other sessions on this branch, off the registry the sidebar already reads.
  const sharedWith = useMemo(() => {
    const branch = code?.branch
    if (!branch) return 0
    const meta =
      (
        projectDoc as
          | { sessionMeta?: Record<string, { branch?: string }> }
          | undefined
      )?.sessionMeta ?? {}
    return Object.entries(meta).filter(
      ([id, m]) => id !== sessionId && String(m?.branch ?? '') === branch,
    ).length
  }, [projectDoc, code, sessionId])

  const projectName =
    projects.find((p) => p.projectId === projectId)?.name ??
    String((projectDoc as { title?: unknown } | undefined)?.title ?? '')
  return (
    <WhereChip
      {...(projectId && projectName
        ? { project: { projectId, name: projectName } }
        : {})}
      projects={projects}
      {...(onSelectProject ? { onSelectProject } : {})}
      devices={presentDevices.map((d) => ({
        deviceId: d.deviceId,
        name: d.name,
        ...(d.kind ? { kind: d.kind } : {}),
      }))}
      selectedDeviceId={selectedDeviceId}
      trees={trees}
      branch={code?.branch ?? null}
      sharedWith={sharedWith}
      opens={opens}
      onSelectDevice={onSelectDevice}
      onSelectWorktree={onSelectWorktree}
      onNewWorktree={onNewWorktree}
      onCheckOutHere={onCheckOutHere}
      {...(projectId && sources.length > 1
        ? {
            otherSources: sources
              .filter((source) => source.id !== mainSourceId)
              .map((source) => (
                <SourceCheckoutLine
                  key={source.id}
                  repo={repo}
                  sessionId={sessionId}
                  projectId={projectId}
                  sourceId={source.id}
                  name={source.name}
                  checkout={sessionCheckoutFor(
                    sessionDoc,
                    home,
                    projectId,
                    source.id,
                  )}
                />
              )),
          }
        : {})}
      {...(projectId
        ? {
            addedProjects: (
              <AddedProjects
                repo={repo}
                sessionId={sessionId}
                homeProjectId={projectId}
                added={added}
                choices={worldProjects.map((p) => ({
                  projectId: p.projectId,
                  name: p.displayName,
                }))}
              />
            ),
          }
        : {})}
    />
  )
}

/**
 * The projects this session also works in (RFC 0362 3c), each with an × to
 * remove it, and the World's other projects to add. Either applies from the
 * next turn; neither moves the session or changes who can open it.
 */
function AddedProjects({
  repo,
  sessionId,
  homeProjectId,
  added,
  choices,
}: {
  repo: Repo
  sessionId: string
  homeProjectId: string
  added: string[]
  choices: Array<{ projectId: string; name: string }>
}) {
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameOf = (id: string) =>
    choices.find((c) => c.projectId === id)?.name ?? id.slice(0, 8)
  const addable = choices.filter(
    (c) => c.projectId !== homeProjectId && !added.includes(c.projectId),
  )
  const run = (work: () => Promise<unknown>) => {
    setError(null)
    work().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : String(e)),
    )
  }
  return (
    <div className="px-3 py-1 text-xs">
      {added.map((id) => (
        <div key={id} className="flex items-center gap-2.5 py-1">
          <Box
            className="size-3.5 shrink-0 text-muted-foreground"
            strokeWidth={1.75}
          />
          <span className="min-w-0 flex-1 truncate text-foreground">
            {nameOf(id)}
          </span>
          <button
            type="button"
            aria-label={`Remove ${nameOf(id)} from this session`}
            onClick={() => run(() => removeSessionProject(repo, sessionId, id))}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </div>
      ))}
      {adding ? (
        addable.map((c) => (
          <button
            key={c.projectId}
            type="button"
            onClick={() => {
              setAdding(false)
              run(() =>
                addSessionProject(repo, sessionId, homeProjectId, c.projectId),
              )
            }}
            className="flex w-full items-center gap-2.5 rounded py-1 text-left text-muted-foreground hover:text-foreground"
          >
            <Box className="size-3.5 shrink-0" strokeWidth={1.75} />
            {c.name}
          </button>
        ))
      ) : addable.length ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-2.5 py-1 text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-3.5" strokeWidth={1.75} />
          Add project
        </button>
      ) : null}
      {error ? <p className="text-[11px] text-red-500">{error}</p> : null}
    </div>
  )
}

/**
 * One of the session's other sources (RFC 0362 3b): its name and the branch
 * the session has of it, or "shared". Branching makes a worktree for this
 * source alone; returning drops the session's entry, and the source reads
 * its shared checkout again. Each applies from the next turn.
 */
function SourceCheckoutLine({
  repo,
  sessionId,
  projectId,
  sourceId,
  name,
  checkout,
}: {
  repo: Repo
  sessionId: string
  projectId: string
  sourceId: string
  name: string
  checkout: SessionCheckout | null
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A new branch starts from this source's own main checkout: without a
  // starting branch the worktree call reads the name as one to fetch.
  const { trees } = useProjectWorktrees(projectId, undefined, sourceId)
  const from = trees.find((t) => t.main)?.branch ?? undefined
  // A fresh name when the session's branch is already there: after Back to
  // shared it still holds the earlier work, and starting on it again would
  // record its head as the base and drop that work from the review.
  const name8 = `mod/session/${sessionId.slice(0, 8)}`
  const taken = new Set(trees.map((t) => t.branch).filter(Boolean))
  let fresh = name8
  for (let n = 2; taken.has(fresh); n++) fresh = `${name8}-${n}`
  const act = (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    work().then(
      () => setBusy(false),
      (e: unknown) => {
        setBusy(false)
        setError(e instanceof Error ? e.message : String(e))
      },
    )
  }
  const branch = () =>
    act(async () => {
      if (!from) throw new Error(`${name} has no checkout on this device`)
      const tree = await addProjectWorktree(projectId, fresh, from, sourceId)
      if (!tree.branch || !tree.head) throw new Error('no branch was made')
      await setSessionCheckout(repo, sessionId, projectId, sourceId, {
        vcs: 'git',
        branch: tree.branch,
        baseCommit: tree.head,
        headCommit: tree.head,
      })
    })
  const share = () =>
    act(() => setSessionCheckout(repo, sessionId, projectId, sourceId, null))
  return (
    <div className="flex items-center gap-2.5 px-3 py-1.5 text-xs">
      <GitBranch
        className="size-3.5 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-foreground">{name}</span>
        <span className="truncate text-[11px] text-muted-foreground">
          {error ?? checkout?.branch ?? 'shared checkout'}
        </span>
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={checkout ? share : branch}
        aria-label={
          checkout ? `Return ${name} to its shared checkout` : `Branch ${name}`
        }
        className="shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        {busy ? '…' : checkout ? 'Back to shared' : 'Branch'}
      </button>
    </div>
  )
}
